/**
 * Integrytas Group - Common Security Module (CSM)
 * useSort - reusable column-sort state with sessionStorage persistence.
 *
 * Pattern:
 *   type UserSortKey = 'email' | 'display_name' | 'is_active';
 *   const sort = useSort<UserSortKey>('csm.users.sort', { key: 'display_name', dir: 'asc' });
 *
 *   const sortedRows = sort.sorted(rows, (a, b, key) => {
 *     // return a number compatible with Array.prototype.sort
 *   });
 *
 *   <SortableHeader sortKey="email" label="Email"
 *     currentKey={sort.sortKey} currentDir={sort.sortDir} onSort={sort.setSort} />
 */

import { useCallback, useEffect, useState } from 'react';
import { scopedKey } from './storage_principal';

export type SortDir = 'asc' | 'desc';

export interface SortState<K extends string> {
  key: K | null;
  dir: SortDir;
}

export interface UseSortResult<K extends string> {
  sortKey: K | null;
  sortDir: SortDir;
  setSort: (key: K) => void;
  clearSort: () => void;
  sorted: <T>(items: T[], compare: (a: T, b: T, key: K) => number) => T[];
}

export function useSort<K extends string>(
  storageKey: string,
  defaultState: SortState<K>,
): UseSortResult<K> {
  // Scoped to the signed-in (or mimicked) user, so a sort chosen by one user is
  // not inherited by the next one to sign in on this browser. Call sites keep
  // passing their plain key — the namespacing happens here.
  const key = scopedKey(storageKey);
  const [state, setState] = useState<SortState<K>>(() => {
    try {
      const raw = sessionStorage.getItem(key);
      if (raw) {
        const parsed = JSON.parse(raw) as SortState<K>;
        if (parsed && (parsed.dir === 'asc' || parsed.dir === 'desc')) return parsed;
      }
    } catch { /* noop */ }
    return defaultState;
  });

  useEffect(() => {
    try { sessionStorage.setItem(key, JSON.stringify(state)); } catch { /* noop */ }
  }, [state, key]);

  const setSort = useCallback((key: K) => {
    setState(prev =>
      prev.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: 'asc' },
    );
  }, []);

  const clearSort = useCallback(() => {
    setState({ key: null, dir: 'asc' });
  }, []);

  const sorted = useCallback(<T,>(
    items: T[],
    compare: (a: T, b: T, key: K) => number,
  ): T[] => {
    if (!state.key) return items;
    const key = state.key;
    const out = [...items].sort((a, b) => compare(a, b, key));
    return state.dir === 'desc' ? out.reverse() : out;
  }, [state]);

  return { sortKey: state.key, sortDir: state.dir, setSort, clearSort, sorted };
}

/**
 * Default comparator for string-ish values. Nullish values sort to the end.
 */
export function compareStrings(a: unknown, b: unknown): number {
  const an = a == null ? '' : String(a);
  const bn = b == null ? '' : String(b);
  if (an === '' && bn !== '') return 1;
  if (bn === '' && an !== '') return -1;
  return an.localeCompare(bn, undefined, { sensitivity: 'base', numeric: true });
}
